import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';

const tourSteps = [
  {
    target: '[data-testid="mobile-menu-btn"], [data-testid="desktop-menu-btn"]',
    title: 'Your Feature Menu',
    description: 'Tap here to access all features — exercises, meal scanning, AI chat, recipes, and more.',
    position: 'bottom',
    color: '#FF6600',
  },
  {
    target: '[data-testid="help-btn"], [data-testid="help-btn-desktop"]',
    title: 'Need Help?',
    description: 'Tap the help icon anytime to replay the onboarding tutorial.',
    position: 'bottom-left',
    color: '#6366f1',
  },
  {
    target: '[data-tour="streaks-widget"]',
    title: 'Your Streaks & Badges',
    description: 'Track your workout and meal streaks here. Stay consistent to earn badges!',
    position: 'bottom',
    color: '#ef4444',
  },
  {
    target: '[data-tour="coaching-nudges"]',
    title: 'AI Coach Tips',
    description: 'Your personal AI coach sends daily tips, reminders, and motivation based on your progress.',
    position: 'bottom',
    color: '#10b981',
  },
  {
    target: '[data-testid="calorie-display"]',
    title: 'Calorie Tracking',
    description: 'See your daily calorie intake at a glance. Scan meals to log calories automatically.',
    position: 'right',
    color: '#f59e0b',
  },
  {
    target: '[data-testid="upgrade-btn"]',
    title: 'Go Premium',
    description: 'Unlock unlimited AI scans, meal plans, and advanced analytics with Premium.',
    position: 'bottom-left',
    color: '#FF6600',
    optional: true,
  },
];

export default function FeatureTour({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [skippedSteps, setSkippedSteps] = useState(0);
  const tooltipRef = useRef(null);

  const findTarget = useCallback((step) => {
    if (!step) return null;
    const selectors = step.target.split(', ');
    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el && el.offsetParent !== null) return el;
    }
    return null;
  }, []);

  const updatePosition = useCallback(() => {
    const step = tourSteps[currentStep];
    if (!step) return;
    const el = findTarget(step);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
      setIsVisible(true);
    } else if (step.optional) {
      // Skip optional steps if target not found
      if (currentStep < tourSteps.length - 1) {
        setCurrentStep(prev => prev + 1);
        setSkippedSteps(prev => prev + 1);
      } else {
        onComplete();
      }
    } else {
      setTargetRect(null);
      setIsVisible(true);
    }
  }, [currentStep, findTarget, onComplete]);

  useEffect(() => {
    const timer = setTimeout(updatePosition, 300);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [updatePosition]);

  const goNext = () => {
    if (currentStep >= tourSteps.length - 1) {
      onComplete();
    } else {
      setIsVisible(false);
      setTimeout(() => setCurrentStep(prev => prev + 1), 150);
    }
  };

  const goPrev = () => {
    if (currentStep > 0) {
      setIsVisible(false);
      setTimeout(() => setCurrentStep(prev => prev - 1), 150);
    }
  };

  const step = tourSteps[currentStep];
  const totalVisible = tourSteps.length - skippedSteps;
  const visibleIndex = currentStep - skippedSteps;
  const isLast = currentStep >= tourSteps.length - 1;

  const getTooltipStyle = () => {
    if (!targetRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const padding = 16;
    const pos = step.position || 'bottom';
    const style = { position: 'fixed' };

    if (pos === 'bottom') {
      style.top = targetRect.bottom + padding;
      style.left = targetRect.left + targetRect.width / 2;
      style.transform = 'translateX(-50%)';
    } else if (pos === 'bottom-left') {
      style.top = targetRect.bottom + padding;
      style.right = window.innerWidth - targetRect.right;
    } else if (pos === 'right') {
      style.top = targetRect.top + targetRect.height / 2;
      style.left = targetRect.right + padding;
      style.transform = 'translateY(-50%)';
    } else if (pos === 'left') {
      style.top = targetRect.top + targetRect.height / 2;
      style.right = window.innerWidth - targetRect.left + padding;
      style.transform = 'translateY(-50%)';
    }

    return style;
  };

  const spotlightPadding = 8;

  return (
    <div
      className="fixed inset-0 z-[100000]"
      data-testid="feature-tour-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) goNext();
      }}
    >
      {/* Dark overlay with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - spotlightPadding}
                y={targetRect.top - spotlightPadding}
                width={targetRect.width + spotlightPadding * 2}
                height={targetRect.height + spotlightPadding * 2}
                rx="12"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0" y="0" width="100%" height="100%"
          fill="rgba(0,0,0,0.65)"
          mask="url(#spotlight-mask)"
          style={{ pointerEvents: 'auto' }}
          onClick={goNext}
        />
      </svg>

      {/* Spotlight ring animation */}
      {targetRect && (
        <div
          className="absolute border-2 rounded-xl animate-pulse pointer-events-none"
          style={{
            left: targetRect.left - spotlightPadding,
            top: targetRect.top - spotlightPadding,
            width: targetRect.width + spotlightPadding * 2,
            height: targetRect.height + spotlightPadding * 2,
            borderColor: step.color,
            boxShadow: `0 0 20px ${step.color}40, 0 0 40px ${step.color}20`,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`max-w-xs w-72 transition-all duration-300 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        style={{ ...getTooltipStyle(), zIndex: 100001 }}
        data-testid={`tour-step-${currentStep}`}
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700">
          {/* Colored header */}
          <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: step.color }}>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-white" />
              <span className="text-white text-xs font-bold uppercase tracking-wider">
                Step {visibleIndex + 1} of {totalVisible}
              </span>
            </div>
            <button
              onClick={onComplete}
              className="text-white/70 hover:text-white transition-colors p-0.5"
              data-testid="tour-skip-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
              {step.title}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Footer */}
          <div className="px-4 pb-4 flex items-center justify-between">
            <div className="flex gap-1.5">
              {tourSteps.filter(s => !s.optional || findTarget(s)).map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === visibleIndex ? 'w-5' : 'w-1.5 bg-gray-200 dark:bg-gray-600'
                  }`}
                  style={i === visibleIndex ? { backgroundColor: step.color } : {}}
                />
              ))}
            </div>
            <div className="flex gap-2">
              {currentStep > 0 && (
                <button
                  onClick={goPrev}
                  className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-400 hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
                  data-testid="tour-prev-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={goNext}
                className="px-4 py-1.5 rounded-lg text-white text-sm font-semibold flex items-center gap-1 hover:opacity-90 transition-opacity"
                style={{ backgroundColor: step.color }}
                data-testid="tour-next-btn"
              >
                {isLast ? 'Got it!' : 'Next'}
                {!isLast && <ChevronRight className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
